import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { apiClient } from "@/lib/api";
import {
  Building2,
  Users,
  Shield,
  CheckCircle2,
  Sparkles,
  Mail,
  Phone,
  Calculator,
} from "lucide-react";

const TEAM_SIZES = [
  { value: "1-10", label: "1–10 employees" },
  { value: "11-50", label: "11–50 employees" },
  { value: "51-200", label: "51–200 employees" },
  { value: "201-500", label: "201–500 employees" },
  { value: "500+", label: "500+ employees" },
];

const PRICE = { team: 13.99, premium: 19.99 };

function teamSizeBucket(seats) {
  if (seats <= 10) return "1-10";
  if (seats <= 50) return "11-50";
  if (seats <= 200) return "51-200";
  if (seats <= 500) return "201-500";
  return "500+";
}

const INITIAL = {
  company_name: "",
  contact_name: "",
  work_email: "",
  phone: "",
  team_size: "",
  industry: "",
  message: "",
};

export default function Business() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // Calculator state
  const [seats, setSeats] = useState(25);
  const [calcPlan, setCalcPlan] = useState("team");
  const monthly = useMemo(
    () => +(seats * PRICE[calcPlan]).toFixed(2),
    [seats, calcPlan]
  );
  const annual = useMemo(() => +(monthly * 12).toFixed(2), [monthly]);
  const fmt = (n) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (fieldErrors[k]) setFieldErrors((fe) => ({ ...fe, [k]: null }));
  };

  const lockInPrice = () => {
    const planLabel = calcPlan === "team" ? "Team ($13.99 / seat)" : "Premium ($19.99 / seat)";
    const summary = `Interested in ${planLabel} for ~${seats} employees (≈ $${fmt(monthly)} / month). Open to a tailored quote.`;
    setForm((f) => ({
      ...f,
      team_size: f.team_size || teamSizeBucket(seats),
      message: f.message ? `${f.message}\n\n${summary}` : summary,
    }));
    // Smooth-scroll to the form on mobile / smaller screens
    document.querySelector("[data-testid='business-inquiry-form']")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const validate = () => {
    const errs = {};
    if (!form.company_name.trim()) errs.company_name = "Required";
    if (!form.contact_name.trim()) errs.contact_name = "Required";
    if (!form.work_email.trim()) errs.work_email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.work_email))
      errs.work_email = "Please enter a valid email";
    if (!form.team_size) errs.team_size = "Pick a size";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const errs = validate();
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/business/inquiry", form);
      setSubmitted(true);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err.message ||
          "Something went wrong — please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-20">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Pitch column */}
          <div className="lg:col-span-5">
            <span className="kicker">For teams</span>
            <h1 className="font-display text-5xl md:text-6xl mt-3 leading-[1.02] tracking-tight">
              Train your <em>whole team</em> on AI.
            </h1>
            <p className="mt-5 text-[var(--ink-2)] text-lg leading-relaxed">
              Roll PromptDojo out across sales, ops, marketing, support — wherever you need people to start using AI well, fast. Tell us about your team and we'll send a tailored quote within one business day.
            </p>

            <div className="mt-8 space-y-4">
              {[
                {
                  icon: Users,
                  title: "Right-sized for your team",
                  desc: "Tailored quote based on team size and rollout — talk to a human, not a calculator.",
                },
                {
                  icon: Building2,
                  title: "Centralized billing",
                  desc: "One invoice for the whole team. Annual or monthly.",
                },
                {
                  icon: Shield,
                  title: "Admin dashboard",
                  desc: "See who's active, who's progressing, and which paths are paying off.",
                },
                {
                  icon: Sparkles,
                  title: "Custom onboarding",
                  desc: "Optional kickoff session and a curated path for your industry.",
                },
              ].map((b) => (
                <div key={b.title} className="flex gap-3">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "var(--emerald)" }}
                  >
                    <b.icon size={16} color="#fff" />
                  </div>
                  <div>
                    <div className="font-medium text-[var(--ink)]">
                      {b.title}
                    </div>
                    <div className="text-sm text-[var(--ink-2)] mt-0.5 leading-relaxed">
                      {b.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-9 brut p-6">
              <div className="flex items-center gap-2 mb-4">
                <Calculator size={15} className="text-[var(--emerald)]" />
                <span className="kicker">Quick estimator</span>
              </div>

              {/* Plan toggle */}
              <div className="inline-flex rounded-full border border-[var(--line-strong)] p-0.5 bg-[var(--bg)]">
                <button
                  type="button"
                  onClick={() => setCalcPlan("team")}
                  className={`inline-flex items-center min-h-[44px] px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${calcPlan === "team" ? "bg-[var(--ink)] text-white" : "text-[var(--ink-2)]"}`}
                  data-testid="calc-plan-team"
                >
                  Team · $13.99
                </button>
                <button
                  type="button"
                  onClick={() => setCalcPlan("premium")}
                  className={`inline-flex items-center min-h-[44px] px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${calcPlan === "premium" ? "bg-[var(--ink)] text-white" : "text-[var(--ink-2)]"}`}
                  data-testid="calc-plan-premium"
                >
                  Premium · $19.99
                </button>
              </div>

              {/* Seat selector */}
              <div className="mt-5">
                <div className="flex items-baseline justify-between mb-2">
                  <label className="text-sm text-[var(--ink-2)]">
                    Number of employees
                  </label>
                  <span
                    className="font-display text-2xl tracking-tight"
                    data-testid="calc-seats-display"
                  >
                    {seats}
                  </span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={500}
                  step={1}
                  value={seats}
                  onChange={(e) => setSeats(Number(e.target.value))}
                  className="w-full accent-[var(--emerald)]"
                  data-testid="calc-seats-slider"
                />
                <div className="flex justify-between text-xs text-[var(--ink-3)] mt-1">
                  <span>1</span>
                  <span>500+</span>
                </div>
              </div>

              {/* Estimate */}
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-[var(--bg)] border border-[var(--line)] p-3.5">
                  <div className="text-xs text-[var(--ink-3)]">Per month</div>
                  <div
                    className="font-display text-3xl tracking-tight mt-0.5 tabular-nums"
                    data-testid="calc-monthly"
                  >
                    ${fmt(monthly)}
                  </div>
                </div>
                <div className="rounded-xl bg-[var(--bg)] border border-[var(--line)] p-3.5">
                  <div className="text-xs text-[var(--ink-3)]">Per year</div>
                  <div
                    className="font-display text-3xl tracking-tight mt-0.5 tabular-nums"
                    data-testid="calc-annual"
                  >
                    ${fmt(annual)}
                  </div>
                </div>
              </div>

              <p className="text-xs text-[var(--ink-3)] mt-3 leading-relaxed">
                Estimate at list price. Send us your details and we'll come back with a tailored quote.
              </p>

              <button
                type="button"
                onClick={lockInPrice}
                className="btn-primary mt-4 w-full justify-center"
                data-testid="calc-lock-in"
              >
                <Sparkles size={14} /> Get a tailored quote →
              </button>
            </div>

            <div className="mt-5 brut p-5 bg-[var(--bg)]">
              <div className="text-sm text-[var(--ink-2)] leading-relaxed">
                Already know what you want? Email us directly at{" "}
                <a
                  href="mailto:teams@promptdojo.app"
                  className="text-[var(--ink)] underline underline-offset-2"
                >
                  teams@promptdojo.app
                </a>
                .
              </div>
            </div>
          </div>

          {/* Form column */}
          <div className="lg:col-span-7">
            <div className="brut-lg p-7 md:p-9">
              {submitted ? (
                <div
                  className="text-center py-8"
                  data-testid="business-inquiry-success"
                >
                  <CheckCircle2
                    size={48}
                    className="mx-auto text-[var(--emerald)]"
                  />
                  <h2 className="font-display text-4xl mt-5 leading-tight tracking-tight">
                    Thanks — we got it.
                  </h2>
                  <p className="mt-3 text-[var(--ink-2)] max-w-md mx-auto leading-relaxed">
                    Someone from our team will reach out to{" "}
                    <span className="text-[var(--ink)]">{form.work_email}</span>{" "}
                    within one business day with a tailored quote and a link to
                    schedule a 15-minute call (only if you want one).
                  </p>
                  <Link
                    to="/"
                    className="btn-ghost mt-7 inline-flex"
                    data-testid="business-inquiry-back-home"
                  >
                    Back to home
                  </Link>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="business-inquiry-form" noValidate>
                  <span className="kicker">Inquiry</span>
                  <h2 className="font-display text-3xl mt-2 leading-tight tracking-tight">
                    Tell us about your team
                  </h2>

                  <div className="mt-6 grid md:grid-cols-2 gap-4">
                    <Field
                      label="Company name"
                      required
                      error={fieldErrors.company_name}
                    >
                      <input
                        type="text"
                        className="brut-input"
                        value={form.company_name}
                        onChange={set("company_name")}
                        placeholder="Acme Corp"
                        autoComplete="organization"
                        data-testid="business-input-company"
                      />
                    </Field>
                    <Field
                      label="Your name"
                      required
                      error={fieldErrors.contact_name}
                    >
                      <input
                        type="text"
                        className="brut-input"
                        value={form.contact_name}
                        onChange={set("contact_name")}
                        placeholder="Jane Doe"
                        autoComplete="name"
                        data-testid="business-input-name"
                      />
                    </Field>
                    <Field
                      label="Work email"
                      required
                      icon={Mail}
                      error={fieldErrors.work_email}
                    >
                      <input
                        type="email"
                        className="brut-input pl-10"
                        value={form.work_email}
                        onChange={set("work_email")}
                        placeholder="you@company.com"
                        autoComplete="email"
                        data-testid="business-input-email"
                      />
                    </Field>
                    <Field label="Phone (optional)" icon={Phone}>
                      <input
                        type="tel"
                        className="brut-input pl-10"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="+1 555 123 4567"
                        autoComplete="tel"
                        data-testid="business-input-phone"
                      />
                    </Field>
                    <Field
                      label="Team size"
                      required
                      error={fieldErrors.team_size}
                    >
                      <select
                        className="brut-input"
                        value={form.team_size}
                        onChange={set("team_size")}
                        data-testid="business-input-team-size"
                      >
                        <option value="">Select a range…</option>
                        {TEAM_SIZES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Industry / role (optional)">
                      <input
                        type="text"
                        className="brut-input"
                        value={form.industry}
                        onChange={set("industry")}
                        placeholder="SaaS · Sales ops"
                        data-testid="business-input-industry"
                      />
                    </Field>
                  </div>

                  <Field label="Anything we should know? (optional)" className="mt-4">
                    <textarea
                      className="brut-input"
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Eg: We have 80 sales reps who need to learn ChatGPT for client emails and CRM notes."
                      data-testid="business-input-message"
                    />
                  </Field>

                  {error && (
                    <div
                      className="mt-4 rounded-xl border border-[rgba(255,92,42,0.3)] bg-[rgba(255,92,42,0.06)] px-4 py-3 text-sm text-[var(--accent)]"
                      data-testid="business-inquiry-error"
                    >
                      {error}
                    </div>
                  )}

                  <div className="mt-6 flex flex-wrap items-center gap-4">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-accent"
                      style={{
                        background: "var(--emerald)",
                        borderColor: "var(--emerald)",
                        boxShadow: "0 6px 24px -8px rgba(14,159,110,0.45)",
                      }}
                      data-testid="business-inquiry-submit"
                    >
                      {submitting ? (
                        <>
                          <span className="spinner" /> Sending…
                        </>
                      ) : (
                        <>
                          <Sparkles size={14} /> Request team pricing
                        </>
                      )}
                    </button>
                    <span className="text-xs text-[var(--ink-3)]">
                      We'll reply within one business day. No spam, no automated
                      drip campaigns.
                    </span>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, required, children, icon: Icon, error, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-[var(--ink)]">
          {label}
          {required && <span className="text-[var(--accent)] ml-0.5">*</span>}
        </span>
        {error && (
          <span className="text-xs text-[var(--accent)]">{error}</span>
        )}
      </div>
      <div className="relative">
        {Icon && (
          <Icon
            size={15}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--ink-3)] pointer-events-none"
          />
        )}
        {children}
      </div>
    </label>
  );
}
